<template>
  <d-container fluid class="main-content-container px-4">
    <!-- Page Header -->
    <d-row no-gutters class="page-header py-4">
      <div class="col-12 text-center text-sm-left mb-0">
        <span class="text-uppercase page-subtitle d-block">
          {{ $t('puzzle.list.subtitle') }}
        </span>
        <h3 class="page-title d-inline">
          {{ $t('puzzle.list.title') }}
        </h3>
        <router-link
          class="btn btn-success text-white float-right"
          :to="{ name: 'admin-crossword-new' }"
        >
          {{ $t('puzzle.list.add') }}
        </router-link>
      </div>
    </d-row>

    <!-- Default Light Table -->
    <d-row>
      <d-col>
        <d-card class="card-small mb-4">
          <div class="card-header border-bottom d-flex justify-content-around">
            <div class="col-6">
              <h6 class="m-0">{{ $t('puzzle.list.tableTitle') }}</h6>
            </div>
            <div class="col-6 d-flex">
              <div class="input-group col-3">
                <d-select
                  v-model="pagination.status"
                  :options="statusOptions"
                  value-field="slug"
                  text-field="name"
                />
              </div>
              <div class="input-group col-3 pl-0">
                <autocomplete
                  ref="templateSearch"
                  :placeholder="$t('puzzle.list.templatePlaceholder')"
                  :items="templates"
                  item-show="name"
                  :on-input="onInputTemplate"
                  :on-select="setSelectedTemplate"
                />
              </div>
              <form class="main-sidebar__search w-100 d-flex">
                <div class="input-group input-group-seamless ml-3">
                  <div class="input-group-prepend">
                    <div class="input-group-text">
                      <i class="fas fa-search"></i>
                    </div>
                  </div>
                  <input
                    class="navbar-search form-control"
                    type="text"
                    :placeholder="$t('puzzle.list.searchPlaceholder')"
                    :aria-label="$t('shared.actions.search')"
                    v-model="pagination.search"
                  />
                </div>
              </form>
            </div>
          </div>
          <div class="card-body p-0 pb-3 text-left">
            <div class="table-overflow">
              <puzzle-list-table
                :puzzles="puzzle.list"
                eventName="Delete:Puzzle"
                highlightTemplate
                :exportable="true"
                :completable="true"
                :sendable="true"
                :showStatus="true"
                :showLevel="true"
                @updateStatusBatch="handleBatchStatusUpdate"
                @sendBatch="handleSendBatch"
              >
                <div slot="actions" slot-scope="{ puzzleId, puzzleName }">
                  <d-link
                    :to="{
                      name: 'admin-puzzle-edit',
                      params: { puzzleId },
                    }"
                  >
                    {{ puzzleName }}
                  </d-link>
                </div>
              </puzzle-list-table>
            </div>
            <pagination
              :total-items="puzzle.total_items"
              :pagination.sync="pagination"
            />
          </div>
        </d-card>
      </d-col>
    </d-row>
  </d-container>
</template>

<script>
import { mapActions } from 'vuex';
import Events from '_utils/events';
import Pagination from '_shared/Pagination.vue';
import PuzzleListTable from '_shared/PuzzleListTable.vue';
import Autocomplete from '_shared/Autocomplete.vue';
import TemplateSearch from '_common/mixins/TemplateSearch';
import formatErrors from '_common/mixins/FormatErrors';
import PuzzleService from '_services/rest/modules/puzzle';
import { STATUS, STATUS_OPTIONS } from '../components/PuzzleModel';

export default {
  name: 'puzzle-list',
  mixins: [TemplateSearch, formatErrors],
  components: {
    Pagination,
    PuzzleListTable,
    Autocomplete,
  },
  data() {
    return {
      pagination: {
        search: '',
        template_id: null,
        status: null,
        slug: 'palavra-cruzadas',
        skip: (this.$route.params.page - 1) * 10,
        take: 10,
        page: parseInt(this.$route.params.page, Number),
      },
    };
  },
  computed: {
    statusOptions() {
      return [
        { slug: null, name: this.$t('puzzle.list.filterStatus') },
        ...STATUS_OPTIONS.map(option => ({
          slug: option.slug,
          name: this.$t(option.labelKey),
        })),
      ];
    },
    puzzle() {
      const { puzzleList } = this.$store.state.puzzle;
      if (puzzleList && puzzleList.list.length) {
        return puzzleList;
      }
      return {
        list: [],
        total_items: 0,
      };
    },
  },
  methods: {
    ...mapActions(['setToggle', 'getPuzzleList', 'activeAlert']),
    getList() {
      this.getPuzzleList(this.pagination);
    },
    handleSendBatch(payload) {
      PuzzleService.send(payload.ids)
        .then(() => {
          this.activeAlert({
            message: this.$t('puzzle.alert.sendSuccess'),
            theme: 'success',
          });
        })
        .catch(() => {
          this.activeAlert({
            message: this.$t('puzzle.alert.sendError'),
            theme: 'danger',
          });
        });
    },
    handleBatchStatusUpdate(payload) {
      const ids = payload.ids || [];
      if (ids.length === 0) return;
      if (payload.status !== STATUS.COMPLETED) return;

      PuzzleService.updateStatus(ids, STATUS.COMPLETED)
        .then(() => {
          const skipped = payload.skippedCount || 0;
          const message =
            skipped > 0
              ? this.$t('puzzle.alert.statusBatchUpdatedWithSkipped', {
                skipped,
              })
              : this.$t('puzzle.alert.statusBatchUpdated');

          this.activeAlert({
            message,
            theme: 'success',
          });

          this.getList();
        })
        .catch(error => {
          const errors = this.formatErrors(error.data);
          this.activeAlert({ message: errors, theme: 'danger' });
        });
    },
    onTemplateCleared() {
      this.pagination.template_id = null;
    },
    setSelectedTemplate(template) {
      if (template) {
        this.pagination.template_id = template.id;
      }
    },
  },
  destroyed() {
    Events.$off('update:pagination');
  },
  mounted() {
    Events.$emit('sidebarVisible', false);
    Events.$on('update:pagination', pagination => {
      this.$router.push({ params: { page: pagination.page } });
      this.getList();
    });
    this.getList();
  },
};
</script>
